// Clean plan structure - no migration needed since not live yet
export type DeploymentTrack = 'cloud' | 'desktop' | 'galbox';
export type BillingPlan = 'free' | 'plus' | 'ultra' | 'pro' | 'business' | 'enterprise' | 'payment_required';

// Model tiers accessible per plan (matches plans.json features_gated.allowedModels)
export type AllowedModelMode = 'fastest' | 'expert' | 'genius';

export type AddonId = 'self_published_book';

export interface AddonInfo {
  id: AddonId;
  name: string;
  description: string;
  monthly_price: number;
  required_plan: BillingPlan;
  features: string[];
}
export type Environment = 'development' | 'production';

export interface PlanInfo {
  track: DeploymentTrack;
  plan: BillingPlan;
  environment?: Environment;
}

export interface PlanFeaturesGated {
  allowedModels: AllowedModelMode[];
  allowIntegrations: boolean;
  allowImagePaste: boolean;
}

export interface PlanDetails {
  name: string;
  description: string;
  monthlyPrice: number;
  aiCredits: number;
  storage: string;
  features: string[];
  features_gated: PlanFeaturesGated;
  // Usage limits - matches backend PLAN_LIMITS exactly
  limits: {
    projects: number;        // total project limit (-1 = unlimited)
    toolCalls: number;
    userMessages: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    acceptedEdits: number;
    imageGenerations: number; // monthly image gen limit
  };
  billing: {
    type: 'free_credits' | 'usage_based' | 'subscription' | 'simple_subscription' | 'unlimited' | 'usage_based_with_included_credits';
    monthlyFreeCredits?: number;
    billingThreshold?: number;
    creditLimit?: number;
    requiresPaymentMethod?: boolean;
  };
}

export interface TrackDetails {
  name: string;
  description: string;
  computeLocation: string;
  pros: string[];
  cons: string[];
  billingAnchor: string;
}

// Note: Plan definitions moved to shared/plans.json and served via API
// Use planConfigService to access plan data instead of hardcoded constants

export const DEPLOYMENT_TRACKS: Record<DeploymentTrack, TrackDetails> = {
  cloud: {
    name: 'River Cloud',
    description: 'Anyone who just wants to log in and start writing with AI help',
    computeLocation: 'Gal servers (AWS)',
    pros: ['Zero-install', 'Works on any device', 'Instant updates'],
    cons: ['Recurring cost', 'Latency vs. local'],
    billingAnchor: 'Subscription'
  },
  desktop: {
    name: 'River Desktop',
    description: 'Power users who want an offline-capable Mac app',
    computeLocation: 'Your Mac\'s CPU/GPU',
    pros: ['Lower latency', 'No browser tab sprawl', 'Works offline for core features'],
    cons: ['Can\'t tap big GPUs', 'Self-updates required'],
    billingAnchor: 'Subscription'
  },
  galbox: {
    name: 'River Box',
    description: 'Creators, devs, and teams who want local-first, high-spec AI & storage',
    computeLocation: 'Dedicated "Box" under your desk',
    pros: ['Zero latency', 'Full privacy', 'Expandable storage/GPUs', 'Runs headless for LAN devices'],
    cons: ['Upfront hardware cost', 'Shipping'],
    billingAnchor: 'Hardware purchase + small OS sub'
  }
};
